import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface HomeContent {
  hero_left_title: string;
  hero_left_subtitle: string;
  hero_left_tagline: string;
  booking_form_title: string;
  booking_form_subtitle: string;
  features_title: string;
  features_items: { icon: string; title: string; text: string }[];
  cta_title: string;
  cta_subtitle: string;
  cta_button_label: string;
  cta_button_link: string;
}

export const DEFAULT_HOME_CONTENT: HomeContent = {
  hero_left_title: "TROPUNCA VIP",
  hero_left_subtitle: "Caribbean Luxury Experience",
  hero_left_tagline: "Private transfers, curated tours & elite service across the Dominican Republic.",
  booking_form_title: "Book Your Transfer",
  booking_form_subtitle: "Instant quote in seconds",
  features_title: "Why Travel With Us",
  features_items: [
    { icon: "Crown", title: "VIP Fleet", text: "Luxury SUVs, vans and sprinters, always spotless." },
    { icon: "Shield", title: "Safe & Insured", text: "Professional bilingual chauffeurs." },
    { icon: "Clock", title: "24/7 Service", text: "Meet & greet at every DR airport." },
  ],
  cta_title: "Ready for the VIP treatment?",
  cta_subtitle: "Reserve your ride in less than a minute.",
  cta_button_label: "Book Now",
  cta_button_link: "/transfers",
};

export const useHomeContent = () => {
  return useQuery({
    queryKey: ["home-content"],
    queryFn: async (): Promise<HomeContent> => {
      const { data, error } = await supabase
        .from("site_config")
        .select("value")
        .eq("key", "home_content")
        .maybeSingle();
      if (error) throw error;
      return { ...DEFAULT_HOME_CONTENT, ...((data?.value as any) || {}) };
    },
  });
};

export const useUpdateHomeContent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: HomeContent) => {
      const { error } = await supabase
        .from("site_config")
        .upsert({ key: "home_content", value: content as any }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["home-content"] });
      toast.success("Home content saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });
};
